import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TicketCheck, Send, Paperclip, AlertCircle,
  Clock, CheckCircle, XCircle, RefreshCw, Download, Trash2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getTicket, addTicketComment, closeTicket, uploadTicketAttachment, deleteTicketAttachment } from '../utils/api';
import './TicketDetailPage.css';

const STATUS_META = {
  open:             { label: 'Open',             color: '#3B82F6' },
  acknowledged:     { label: 'Acknowledged',     color: '#8B5CF6' },
  in_progress:      { label: 'In Progress',      color: '#F59E0B' },
  waiting_on_user:  { label: 'Waiting on You',   color: '#EF4444' },
  resolved:         { label: 'Resolved',         color: '#10B981' },
  closed:           { label: 'Closed',           color: '#6B7280' },
  wont_fix:         { label: "Won't Fix",        color: '#DC2626' },
};

const CATEGORIES = [
  { value: 'wrong_data',  label: 'Wrong / Unexpected Data' },
  { value: 'ui_bug',      label: 'UI / Display Issue' },
  { value: 'performance', label: 'Slow Performance' },
  { value: 'access',      label: 'Access / Login Issue' },
  { value: 'feature',     label: 'Feature Request' },
  { value: 'other',       label: 'Other' },
];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: '#94A3B8' };
  return (
    <span className="td-badge" style={{ '--badge-color': meta.color }}>
      {meta.label}
    </span>
  );
}

function fmt(iso) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function TicketDetailPage() {
  const { ref } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [ticket,     setTicket]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  const [comment,    setComment]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commentErr, setCommentErr] = useState(null);

  const [closing,      setClosing]      = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [uploadErr,    setUploadErr]    = useState(null);
  const [deletingAtt,  setDeletingAtt]  = useState(null); // att id being deleted

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTicket(ref);
      setTicket(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => { load(); }, [load]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmitting(true);
    setCommentErr(null);
    try {
      const c = await addTicketComment(ref, comment.trim());
      setTicket((t) => ({ ...t, comments: [...t.comments, c], updated_at: new Date().toISOString() }));
      setComment('');
    } catch (e) {
      setCommentErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      await closeTicket(ref);
      setTicket((t) => ({ ...t, status: 'closed' }));
    } catch (e) {
      setError(e.message);
    } finally {
      setClosing(false);
    }
  };

  const handleDeleteAttachment = async (attId) => {
    setDeletingAtt(attId);
    try {
      await deleteTicketAttachment(ref, attId);
      setTicket((t) => ({ ...t, attachments: t.attachments.filter((a) => a.id !== attId) }));
    } catch (e) {
      setUploadErr(e.message);
    } finally {
      setDeletingAtt(null);
    }
  };

  const MAX_FILE_MB = 5;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setUploadErr(`File too large. Maximum size is ${MAX_FILE_MB} MB.`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    setUploadErr(null);
    try {
      const att = await uploadTicketAttachment(ref, file);
      setTicket((t) => ({ ...t, attachments: [...t.attachments, att] }));
    } catch (e) {
      setUploadErr(e.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const isResolved = ticket?.status === 'resolved';
  const isClosed   = ticket?.status === 'closed' || ticket?.status === 'wont_fix';

  if (loading) return <div className="td-page td-center">Loading ticket…</div>;
  if (error)   return <div className="td-page td-center td-error"><AlertCircle size={16} /> {error}</div>;
  if (!ticket) return null;

  const categoryLabel = CATEGORIES.find((c) => c.value === ticket.category)?.label || ticket.category;

  return (
    <div className="td-page">
      {/* Back nav */}
      <button className="td-back" onClick={() => navigate('/support')}>
        <ArrowLeft size={15} /> Back to tickets
      </button>

      {/* Header card */}
      <div className="td-header-card">
        <div className="td-header-top">
          <div className="td-ref-row">
            <TicketCheck size={18} className="td-ref-icon" />
            <span className="td-ref">{ticket.ticket_ref}</span>
            <StatusBadge status={ticket.status} />
          </div>
          <button className="td-btn td-btn--ghost td-refresh" onClick={load} title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>
        <h1 className="td-title">{ticket.title}</h1>
        <div className="td-meta">
          <span className="td-meta-chip">{categoryLabel}</span>
          <span className="td-meta-chip td-priority" data-priority={ticket.priority}>{ticket.priority}</span>
          {user?.role === 'admin' && (
            <span className="td-meta-user">{ticket.user_name || ticket.user_email}</span>
          )}
          <span className="td-meta-date"><Clock size={11} /> {fmt(ticket.created_at)}</span>
        </div>
        <p className="td-description">{ticket.description}</p>

        {/* Close button — only for resolved tickets */}
        {isResolved && (
          <div className="td-action-row">
            <button className="td-btn td-btn--success" onClick={handleClose} disabled={closing}>
              <CheckCircle size={14} /> {closing ? 'Closing…' : 'Mark as Closed'}
            </button>
          </div>
        )}
        {isClosed && (
          <div className="td-closed-note">
            <XCircle size={13} /> This ticket is {STATUS_META[ticket.status]?.label?.toLowerCase()}.
          </div>
        )}
      </div>

      {/* Attachments */}
      {ticket.attachments.length > 0 && (
        <div className="td-section">
          <h3 className="td-section-title">
            Attachments <span className="td-section-count">{ticket.attachments.length}</span>
          </h3>
          <div className="td-attachments">
            {ticket.attachments.map((a) => (
              <div key={a.id} className="td-attachment-row">
                {a.blob_url ? (
                  <a
                    href={a.blob_url}
                    target="_blank"
                    rel="noreferrer"
                    className="td-attachment"
                    title="Download"
                  >
                    <Paperclip size={13} />
                    <span className="td-att-name">{a.filename}</span>
                    <span className="td-att-size">{fmtSize(a.file_size)}</span>
                    <Download size={12} />
                  </a>
                ) : (
                  <span className="td-attachment" style={{ cursor: 'default', opacity: 0.7 }} title="File stored — download link pending">
                    <Paperclip size={13} />
                    <span className="td-att-name">{a.filename}</span>
                    <span className="td-att-size">{fmtSize(a.file_size)}</span>
                  </span>
                )}
                {!isClosed && (
                  <button
                    className="td-att-delete"
                    onClick={() => handleDeleteAttachment(a.id)}
                    disabled={deletingAtt === a.id}
                    title="Remove attachment"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comment thread */}
      <div className="td-section">
        <h3 className="td-section-title">
          Comments <span className="td-section-count">{ticket.comments.length}</span>
        </h3>
        {ticket.comments.length === 0 ? (
          <p className="td-no-comments">No comments yet.</p>
        ) : (
          <div className="td-comments">
            {ticket.comments.map((c) => (
              <div
                key={c.id}
                className={`td-comment ${c.author_role === 'inqlect' ? 'td-comment--inqlect' : 'td-comment--user'}`}
              >
                <div className="td-comment-header">
                  <span className="td-comment-author">
                    {c.author_name || c.author_email}
                  </span>
                  {c.author_role === 'inqlect' && (
                    <span className="td-comment-tag">InqLect Support</span>
                  )}
                  {c.author_role === 'tenant_admin' && (
                    <span className="td-comment-tag td-comment-tag--admin">Admin</span>
                  )}
                  <span className="td-comment-date">{fmt(c.created_at)}</span>
                </div>
                <p className="td-comment-body">{c.body}</p>
              </div>
            ))}
          </div>
        )}

        {/* Add comment form — disabled when ticket is closed/wont_fix */}
        {!isClosed && (
          <form className="td-comment-form" onSubmit={handleComment}>
            <textarea
              className="td-textarea"
              rows={3}
              placeholder="Add a comment…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={5000}
            />
            {commentErr && (
              <div className="td-form-error"><AlertCircle size={13} /> {commentErr}</div>
            )}
            <div className="td-form-actions">
              {/* File upload */}
              <label className="td-btn td-btn--ghost td-upload-label" title="Attach file">
                <Paperclip size={14} />
                {uploading ? 'Uploading…' : 'Attach'}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="td-file-input"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </label>
              {uploadErr && <span className="td-upload-err">{uploadErr}</span>}
              <button type="submit" className="td-btn td-btn--primary" disabled={submitting || !comment.trim()}>
                <Send size={13} /> {submitting ? 'Sending…' : 'Send'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
